import { Component, Input, OnInit } from '@angular/core';
import { isAdmin } from '@era/core';
import { DeviceTask } from '@era/device-task-management-sdk';
import { DeviceSyncStatus, SyncType } from '@era/locations-sdk';
import { DeviceStoreActions } from '@era/stores';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, map, Observable } from 'rxjs';
import { ForceSyncService } from '../device-mdm-sync-force/device-mdm-sync-force.service';
import { DeviceSyncStatusRetryComponent } from './device-sync-status-retry.component';
import { AsyncPipe } from '@angular/common';
import { DeviceScheduledTasksStoreSelectors } from '../../sub-features/device-sync/device-scheduled-tasks-store';

@Component({
  selector: 'era-device-sync-status-retry',
  templateUrl: './device-sync-status-retry.container.html',
  styleUrls: ['./device-sync-status-retry.container.scss'],
  imports: [DeviceSyncStatusRetryComponent, AsyncPipe],
})
export class DeviceSyncStatusRetryContainerComponent implements OnInit {
  SyncType = SyncType;
  private forceSync = false;

  @Input() public currentSyncType: SyncType;
  public allowRetry$: Observable<boolean>;
  private currentType: string;
  private readonly scheduledTasks$: Observable<DeviceTask[]>;
  private readonly inQueue$: Observable<boolean>;
  private readonly isAdmin$: Observable<boolean>;

  constructor(
    private readonly deviceTaskStore: Store<DeviceTask>,
    private readonly deviceSyncStatusStore: Store<DeviceSyncStatus>,
    private readonly oidcService: OidcSecurityService,
    private forceSyncService: ForceSyncService
  ) {
    this.scheduledTasks$ = this.deviceTaskStore.select(DeviceScheduledTasksStoreSelectors.selectScheduledTasksByUdid);
    this.inQueue$ = this.scheduledTasks$.pipe(map((data) => data.some((x) => x.task === this.currentType)));
    this.isAdmin$ = this.oidcService.userData$.pipe(map((data) => isAdmin(data)));
  }

  ngOnInit() {
    this.forceSyncService.forceSync$.subscribe((value) => {
      this.forceSync = value;
    });

    this.currentType = this.convertSyncType(this.currentSyncType);

    if (this.currentSyncType === SyncType.WifiProfiles) {
      this.allowRetry$ = combineLatest([this.isAdmin$, this.inQueue$]).pipe(
        map(([isAdmin, inQueue]) => isAdmin && !inQueue)
      );
    } else {
      this.allowRetry$ = this.inQueue$.pipe(map((data) => !data));
    }
  }

  public scheduleTask(event: SyncType): void {
    this.deviceSyncStatusStore.dispatch(
      DeviceStoreActions.DeviceSyncStatusViewActions.retryGivenSync({ syncType: event, forceSync: this.forceSync })
    );
  }

  private convertSyncType(syncType: SyncType): string {
    switch (syncType) {
      case SyncType.AirplaySecurityProfile:
        return 'airplay-sync';
      case SyncType.AppLockProfile:
        return 'applock-sync';
      case SyncType.Applications:
        return 'application-sync';
      case SyncType.ConfigurationProfiles:
        return 'configuration-sync';
      case SyncType.DeviceName:
        return 'devicename-sync';
      case SyncType.EraApplicationConfiguration:
        return 'application-configurations-sync';
      case SyncType.NotificationProfiles:
        return 'notification-sync';
      case SyncType.RestrictionsProfiles:
        return 'restrictions-sync';
      case SyncType.WifiProfiles:
        return 'wifi-sync';
      default:
        return '';
    }
  }
}
